import { z } from 'zod';
import { AudienceDefinition } from './audience.ts';
import { ScheduledDelivery } from './campaigns.ts';
import { MessageExemptions, QuietHours, RATE_DELAY_MAX_HOURS } from './delivery-controls.ts';
import { PreviewProfile } from './templating.ts';
import { TriggerSpec } from './triggers.ts';

export const STEP_TYPES = ['message', 'delay', 'action_paths', 'audience_paths', 'decision_split', 'context', 'user_update', 'experiment_paths', 'exit'] as const;
export type StepType = (typeof STEP_TYPES)[number];

export const StepId = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, 'step_id_invalid');

/** One outgoing edge: the handler decides which `port` an entry leaves through; `stepId: null` ends the journey. */
export const NextLink = z.strictObject({ port: z.string().min(1).max(64), stepId: StepId.nullable() });

export const Step = z.strictObject({
  id: StepId,
  type: z.enum(STEP_TYPES),
  name: z.string().trim().min(1).max(120),
  /** Validated by the step handler registered for `type`. */
  config: z.unknown(),
  next: z.array(NextLink).max(20),
});

/** A variant routes `percent` of entries to `startStepId`; `null` is a control group (entries exit immediately). */
export const Variant = z.strictObject({
  id: StepId,
  name: z.string().trim().min(1).max(80),
  percent: z.number().int().min(0).max(100),
  startStepId: StepId.nullable(),
});

/**
 * Canvas graph (BB-121). `layout` is editor-only and never affects the runtime. Entry, audience, send settings,
 * conversions and exit criteria live next to the definition on the version (their schemas belong to BB-111…BB-113).
 */
export const CanvasDefinition = z.strictObject({
  schemaVersion: z.literal(1),
  variants: z.array(Variant).min(1).max(8),
  steps: z.record(StepId, Step),
  layout: z.record(StepId, z.strictObject({ x: z.number(), y: z.number() })).optional(),
});

export type NextLink = z.infer<typeof NextLink>;
export type Step = z.infer<typeof Step>;
export type Variant = z.infer<typeof Variant>;
export type CanvasDefinition = z.infer<typeof CanvasDefinition>;

/** Canvas statuses as stored (BB-121); the list adds the derived `idle`. */
export const CANVAS_STATUSES = ['draft', 'active', 'paused_entries', 'paused_all', 'stopped', 'archived'] as const;
export type CanvasStatus = (typeof CANVAS_STATUSES)[number];
/** Entry types of the published version (`entry.type`, BB-111); `null` before publish or when not set. */
export const CANVAS_ENTRY_TYPES = ['scheduled', 'action_based', 'api_triggered'] as const;
export const CANVAS_SORTS = ['updated', 'created', 'name'] as const;
export const CANVAS_LIST_ACTIONS = ['duplicate', 'archive', 'restore'] as const;

/** List filters (BB-109): no `status` lists everything but archived; `paused` covers both pause modes. */
export const CanvasListQuery = z.object({
  query: z.string().trim().max(200).optional(),
  status: z.enum(['draft', 'active', 'paused', 'stopped', 'archived', 'idle', 'all']).optional(),
  entryType: z.enum(CANVAS_ENTRY_TYPES).optional(),
  tagId: z.uuid().optional(),
  sort: z.enum(CANVAS_SORTS).default('updated'),
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type CanvasListQuery = z.infer<typeof CanvasListQuery>;

/** A new draft Canvas: empty, or a copy of a template's graph (BB-56 will bring saved templates). */
export const CreateCanvasBody = z.strictObject({ name: z.string().trim().min(1).max(200), templateId: z.string().min(1).max(64).optional() });
export type CreateCanvasBody = z.infer<typeof CreateCanvasBody>;

export interface CanvasPerson {
  id: string;
  email: string | null;
}

export interface CanvasSummary {
  id: string;
  apiId: string;
  name: string;
  status: CanvasStatus;
  /** Active, but nothing sent within the idle threshold (`settings.campaignIdleDays`, default 30). */
  idle: boolean;
  entryType: (typeof CANVAS_ENTRY_TYPES)[number] | null;
  tags: Array<{ id: string; name: string; color: string | null }>;
  /** Sends counted by the daily reporting aggregates (test sends excluded). */
  sent: number;
  /** Entries in progress (active or waiting) and failed ones. */
  activeEntries: number;
  failedEntries: number;
  /** An enabled monitoring rule watches it (BB-107). */
  alertsConfigured: boolean;
  publishedVersion: number | null;
  stepCount: number;
  createdBy: CanvasPerson | null;
  updatedBy: CanvasPerson | null;
  createdAt: string;
  updatedAt: string;
  firstPublishedAt: string | null;
  lastSentAt: string | null;
  stoppedAt: string | null;
  archivedAt: string | null;
  actions: Array<(typeof CANVAS_LIST_ACTIONS)[number]>;
}

export interface CanvasList {
  items: CanvasSummary[];
  nextCursor: string | null;
  idleCount: number;
  idleDays: number;
}

export interface CanvasTemplateView {
  id: string;
  name: string;
  description: string;
  stepCount: number;
}

export interface CanvasView extends CanvasSummary {
  stopReason: string | null;
  /** Steps of the draft graph (comment anchors, node counters until the graph editor, BB-114). */
  steps: Array<{ id: string; name: string; type: string }>;
  versions: Array<{ id: string; version: number; publishedAt: string; publishedBy: CanvasPerson | null }>;
}

/** Saves the draft graph from the editor (BB-114); `baseUpdatedAt` guards concurrent edits (412 `revision_conflict`). */
export const SaveCanvasDraftBody = z.strictObject({ definition: CanvasDefinition, baseUpdatedAt: z.iso.datetime() });
export type SaveCanvasDraftBody = z.infer<typeof SaveCanvasDraftBody>;
/** Validates the given graph (unsaved editor state) or the stored draft. */
export const ValidateCanvasDraftBody = z.strictObject({ definition: z.unknown().optional() }).default({});

export interface CanvasDraftView {
  definition: CanvasDefinition;
  updatedAt: string;
  /** The running version's graph, for locks and «what changed». */
  published: { version: number; definition: Omit<CanvasDefinition, 'layout'> } | null;
}

export interface CanvasValidationIssue {
  code: string;
  message: string;
  stepId?: string;
  severity: 'error' | 'warning';
  /** The wizard step the issue belongs to (BB-110); absent for graph issues of a step. */
  section?: 'entry' | 'audience' | 'controls' | 'exit' | 'send' | 'build';
  /** `invalid_step_config`: the failing config fields (`path` inside the step's `config`). */
  fields?: Array<{ path: string; message: string }>;
}

export interface CanvasValidation {
  valid: boolean;
  issues: CanvasValidationIssue[];
  /** One-line summary of each step (node subtitle in detailed view). */
  summaries: Record<string, string>;
  /** Longest time an entry can spend in the Canvas; `null` with a cycle. */
  maxDurationSeconds: number | null;
  /** Steps added, removed or changed against the running version (BB-110); null before the first publish. */
  diff: { added: string[]; removed: string[]; changed: string[] } | null;
  /** Steps of the running version that the draft removes while entries are in them: publishing with `migrate` must map them. */
  requiredMigrations: Array<{ stepId: string; entries: number }>;
}

/** Launch or update (BB-110): `pin` keeps running entries on their version; `migrate` moves them, mapping removed steps. */
export const PublishCanvasBody = z
  .strictObject({
    migration: z
      .discriminatedUnion('mode', [z.strictObject({ mode: z.literal('pin') }), z.strictObject({ mode: z.literal('migrate'), map: z.record(StepId, z.union([StepId, z.literal('exit')])).default({}) })])
      .default({ mode: 'pin' }),
  })
  .prefault({});
export type PublishCanvasBody = z.infer<typeof PublishCanvasBody>;

export interface CanvasPublishResult {
  version: number;
  /** False when nothing changed since the running version. */
  created: boolean;
  migrated: number;
}

/** `entries` — no new entries, running ones go on; `all` — everything waits until resumed (BB-121). */
export const PauseCanvasBody = z.strictObject({ mode: z.enum(['entries', 'all']) });
export const StopCanvasBody = z.strictObject({ reason: z.string().trim().min(1).max(500) });

/** A step type of the editor palette (BB-114). `runtime: false` — designable, sent once its ticket ships the handler. */
export interface CanvasStepTypeView {
  type: StepType;
  label: string;
  category: 'Message Controls' | 'Flow Controls' | 'Audience Updates';
  description: string;
  runtime: boolean;
  defaultConfig: Record<string, unknown>;
  defaultPorts: string[];
}

/** Delay step preview (BB-116): when an entry reaching the step now (or at `at`) would wake up, for a test profile. */
export const DelayPreviewBody = z.strictObject({
  config: z.unknown(),
  /** The profile whose timezone and attributes to use; without it, the environment timezone and no attributes. */
  profileId: z.uuid().optional(),
  /** The same choice as message previews (a profile, an external ID, a random user); wins over `profileId`. */
  profile: PreviewProfile.optional(),
  /** Context variables of the entry (personalized delays). */
  context: z.record(z.string(), z.unknown()).default({}),
  at: z.iso.datetime().optional(),
});
export type DelayPreviewBody = z.infer<typeof DelayPreviewBody>;

/** Preview of a Context step (BB-119): its variables rendered in order for one profile and an example event. */
export const ContextPreviewBody = z.strictObject({
  config: z.unknown(),
  /** The profile whose attributes `user.*` reads; without it, `user.*` is empty. */
  profile: PreviewProfile.optional(),
  /** The entry's context before the step (API trigger context, earlier Context steps). */
  context: z.record(z.string(), z.unknown()).default({}),
  /** Properties of the event that moved the entry (`event_properties.*`). */
  eventProperties: z.record(z.string(), z.unknown()).optional(),
  at: z.iso.datetime().optional(),
});
export type ContextPreviewBody = z.infer<typeof ContextPreviewBody>;

export interface ContextPreview {
  /** The context after the step: earlier values plus the variables that rendered and converted. */
  context: Record<string, unknown>;
  updated: string[];
  /** Variables that keep their old value, with why (`aborted`, `not_a_number`, `template_error: …`, `context_too_large`…). */
  notUpdated: Array<{ name: string; reason: string }>;
  /** Personal attributes were shown as ••• (no `pii.view`). */
  masked: boolean;
  profileId?: string;
}

export interface DelayPreview {
  summary: string;
  /** `wait` until `wakeAt`; `advance_now`; or `exit` with `reason` (`delay_past`, `personalization_failed`). */
  outcome: 'wait' | 'advance_now' | 'exit';
  wakeAt: string | null;
  zone: string | null;
  reason?: string;
  /** The profile the preview used. */
  profileId?: string;
}

/**
 * How users enter a Canvas (BB-111, CAN-002/CAN-012), stored with each version (`canvas_version.entry`) and edited in
 * the draft settings: on a schedule for the audience at run time, when they do something (BB-100 triggers, any of them,
 * within an optional window), or through the Canvas Trigger API (BB-124). `controls` say who may enter again and how many.
 */
export const ENTRY_LIMIT_PERIODS = ['hour', 'day', 'lifetime', 'run'] as const;

export const EntryControls = z.strictObject({
  /** Off: a profile enters once. On: again once `windowSeconds` passed since its last entry. */
  reentry: z.strictObject({ enabled: z.boolean().default(false), windowSeconds: z.number().int().min(0).max(730 * 86_400).default(0) }).default({ enabled: false, windowSeconds: 0 }),
  /** Several entries of one profile at once (each transaction its own pass, CP-01); otherwise one active entry. */
  allowConcurrent: z.boolean().default(false),
  /** At most `count` entries per UTC hour, UTC day, scheduled run, or ever. */
  limit: z.strictObject({ count: z.number().int().min(1).max(100_000_000), per: z.enum(ENTRY_LIMIT_PERIODS) }).optional(),
});
export type EntryControls = z.infer<typeof EntryControls>;

export const CanvasEntrySpec = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('scheduled'), schedule: ScheduledDelivery }),
  z.strictObject({
    type: z.literal('action_based'),
    /** Any of them enters the user. */
    triggers: z.array(TriggerSpec).min(1).max(10),
    /** Actions before `startAt` or after `endAt` do not enter. */
    window: z.strictObject({ startAt: z.iso.datetime().optional(), endAt: z.iso.datetime().optional() }).default({}),
  }),
  z.strictObject({ type: z.literal('api_triggered') }),
]);
export type CanvasEntrySpec = z.infer<typeof CanvasEntrySpec>;

/**
 * When users leave a Canvas early (BB-112, CAN-009): an exit event (BB-100 trigger) happening after they entered,
 * optionally matching the entry context (`event property == context key`, e.g. the same transaction); or matching the
 * exit audience, checked before each step. Stored in `canvas_version.exit_criteria`.
 */
export const ExitEvent = z.strictObject({
  trigger: TriggerSpec,
  /** Only an event about this entry: each event property must equal the entry's context value. */
  correlate: z.array(z.strictObject({ property: z.string().min(1).max(255), contextKey: z.string().min(1).max(255) })).max(5).default([]),
});
export type ExitEvent = z.infer<typeof ExitEvent>;

export const CanvasExitCriteria = z.strictObject({
  events: z.array(ExitEvent).max(10).default([]),
  /** Users who come to match it leave at their next step. */
  audience: AudienceDefinition.nullable().default(null),
});
export type CanvasExitCriteria = z.infer<typeof CanvasExitCriteria>;

/**
 * Send settings of a Canvas (BB-113): the delivery controls of every Message step — quiet hours, a sending rate for the
 * whole Canvas, and what its messages may skip (`messages.exemptions.manage` to change). A Message step may override
 * quiet hours and exemptions. Global frequency caps, suppression lists and consent apply as for campaigns.
 */
export const CanvasSendSettings = z.strictObject({
  quietHours: QuietHours.optional(),
  /** Sends per minute of the whole Canvas (all its steps); the environment's channel limit applies too. */
  ratePerMinute: z.number().int().min(1).max(1_000_000).optional(),
  rateMaxDelayHours: z.number().int().min(1).max(RATE_DELAY_MAX_HOURS).optional(),
  exemptions: MessageExemptions.default({}),
});
export type CanvasSendSettings = z.infer<typeof CanvasSendSettings>;

/** Draft entry settings of a Canvas (`canvas.draft_settings`); publish copies them into the version. */
export const CanvasSettings = z.strictObject({
  entry: CanvasEntrySpec.nullable().default(null),
  /** Checked when the user enters, never later (Braze too). Null — everyone. */
  audience: AudienceDefinition.nullable().default(null),
  controls: EntryControls.prefault({}),
  exit: CanvasExitCriteria.prefault({}),
  send: CanvasSendSettings.prefault({}),
});
export type CanvasSettings = z.infer<typeof CanvasSettings>;

export const ENTRY_REJECTIONS = ['canvas_not_found', 'canvas_not_accepting', 'not_published', 'excluded', 'context_too_large', 'audience', 'already_active', 'not_reeligible', 'reentry_window', 'entry_limit'] as const;
export type EntryRejection = (typeof ENTRY_REJECTIONS)[number];

/** Canvas analytics (BB-123): what happened in each step over a period, and who is in it now. */
export const CanvasAnalyticsQuery = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
    versionId: z.uuid().optional(),
    variantId: z.string().min(1).max(64).optional(),
  })
  .refine((q) => q.from <= q.to, { message: 'from must not be after to', path: ['from'] })
  .refine((q) => (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000 <= 366, { message: 'at most 366 days', path: ['to'] });
export type CanvasAnalyticsQuery = z.infer<typeof CanvasAnalyticsQuery>;

export interface CanvasStepStats {
  stepId: string;
  /** The step's stable uuid (`canvasStepUuid`): the `canvasStepId` of its facts and of push analytics filters. */
  stepUuid: string;
  name: string;
  type: StepType;
  /** Arrivals in the period. */
  entered: number;
  /** In the step now (not limited to the period). */
  waiting: number;
  /** Departures by port (`everyone_else`, a group id, `next`…). */
  advanced: Record<string, number>;
  /** Exits from the step by reason (`message_suppressed`, `exit_event:…`, `action_paths_exit`…). */
  exited: Record<string, number>;
  failed: number;
  /** Message steps: sends handed to a channel (`enqueued` facts). */
  sends: number;
  /** Time from arriving to leaving, for entries that left in the period. */
  timeInStep: { p50Seconds: number | null; p90Seconds: number | null };
}

export interface CanvasAnalytics {
  from: string;
  to: string;
  kpis: { entries: number; completed: number; exited: number; failed: number; waiting: number };
  variants: Array<{ variantId: string; entries: number }>;
  steps: CanvasStepStats[];
}

/** An entry waiting in a step much longer than the step can hold it (a lost wake-up, a broken event wait). */
export interface CanvasStuckEntry {
  entryId: string;
  profileId: string;
  stepId: string;
  waitingSince: string;
  /** When the step should have let it go (its longest wait plus an hour). */
  expectedBy: string;
}

/**
 * Test Canvas simulator (BB-122, CAN-011): one profile through the Canvas on a virtual clock — the real runtime and
 * step handlers, scripted events at relative times, a trace of every transition. Nothing persists and nothing is sent:
 * `dry_run` renders each Message step's exact payload instead of handing it to a sender. `test_send` (real test
 * messages to a test profile's devices) comes with BB-59.
 */
export const SIMULATION_LIMITS = { events: 500, stepExecutions: 2000, timeoutMs: 10_000, untilDays: 800 } as const;

const DURATION_UNITS_MS = { s: 1000, m: 60_000, h: 3_600_000, d: 86_400_000, w: 604_800_000 } as const;
const DURATION = /^(\d{1,6})(s|m|h|d|w)$/;

/** Milliseconds of a simulation duration (`90m`, `3d`, `+3d` as an offset); `null` when it is not one. */
export function simulationDurationMs(value: string): number | null {
  const match = DURATION.exec(value.startsWith('+') ? value.slice(1) : value);
  return match ? Number(match[1]) * DURATION_UNITS_MS[match[2] as keyof typeof DURATION_UNITS_MS] : null;
}

/** When a scripted event happens: an offset from the simulation start (`+5m`, `+3d`) or an ISO time. */
export const SimulationTime = z.union([z.string().regex(/^\+\d{1,6}(s|m|h|d|w)$/, 'simulation_offset_invalid'), z.iso.datetime({ offset: true })]);
const Properties = z.record(z.string(), z.unknown());

/**
 * A scripted occurrence of the simulated profile, in the BB-100 trigger vocabulary: a custom event, an attribute change
 * (the profile is updated, as ingest would; standard attributes take their `$` key), a session start, a subscription
 * change or a message interaction.
 */
export const SimulationEvent = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('custom_event'), at: SimulationTime, name: z.string().min(1).max(255), properties: Properties.default({}) }),
  z.strictObject({ type: z.literal('attribute_change'), at: SimulationTime, attribute: z.string().min(1).max(255), value: z.unknown() }),
  z.strictObject({ type: z.literal('session_start'), at: SimulationTime, appInstanceId: z.uuid().nullable().default(null) }),
  z.strictObject({ type: z.literal('subscription_change'), at: SimulationTime, groupId: z.uuid(), state: z.enum(['subscribed', 'unsubscribed']) }),
  z.strictObject({ type: z.literal('message_interaction'), at: SimulationTime, messageApiId: z.uuid(), interaction: z.enum(['click', 'open', 'impression']) }),
]);
export type SimulationEvent = z.infer<typeof SimulationEvent>;

/** A made-up profile for the run (never stored): standard attributes, custom attributes and the random bucket. */
export const SimulationProfileSnapshot = z.strictObject({
  /** Rendered as `user.external_id`; not stored as an identity. */
  externalId: z.string().min(1).max(512).optional(),
  email: z.string().max(512).optional(),
  phone: z.string().max(64).optional(),
  firstName: z.string().max(255).optional(),
  lastName: z.string().max(255).optional(),
  language: z.string().max(35).optional(),
  country: z.string().max(2).optional(),
  timezone: z.string().max(64).optional(),
  dateOfBirth: z.iso.date().optional(),
  customAttributes: Properties.default({}),
  /** 0–9999 (random splits, the global control group); random when left out. */
  randomBucket: z.number().int().min(0).max(9999).optional(),
});
export type SimulationProfileSnapshot = z.infer<typeof SimulationProfileSnapshot>;

export const SimulationBody = z.strictObject({
  /** The saved draft (as the editor last saved it, with the draft settings) or the running version. */
  definitionSource: z.enum(['draft', 'published']).default('draft'),
  /** A member of a Content test internal group (BB-19) with their real data and devices, or a synthetic profile. */
  profile: z.union([z.strictObject({ testProfileId: z.uuid() }), z.strictObject({ synthetic: SimulationProfileSnapshot })]),
  /**
   * How the profile enters at the start: the entry context (API trigger context), the event of an action-based entry
   * (its properties become the context and `event_properties`), and the variant (by default the one the running
   * Canvas would assign to a test profile; random for a synthetic one).
   */
  entry: z
    .strictObject({
      context: Properties.default({}),
      event: z.strictObject({ name: z.string().min(1).max(255), properties: Properties.default({}) }).optional(),
      variantId: z.string().min(1).max(64).optional(),
    })
    .prefault({}),
  events: z.array(SimulationEvent).max(SIMULATION_LIMITS.events).default([]),
  /** Virtual start; now by default. */
  startAt: z.iso.datetime({ offset: true }).optional(),
  /**
   * How far the virtual clock may run (`31d`, at most 800 days); by default the Canvas's longest path plus a day (for
   * Action Paths grace and quiet-hours retries).
   */
  untilDuration: z
    .string()
    .regex(DURATION, 'simulation_duration_invalid')
    .refine((v) => (simulationDurationMs(v) ?? 0) > 0 && (simulationDurationMs(v) ?? 0) <= SIMULATION_LIMITS.untilDays * 86_400_000, 'simulation_duration_out_of_range')
    .optional(),
  deliveryMode: z.enum(['dry_run', 'test_send']).default('dry_run'),
});
export type SimulationBody = z.infer<typeof SimulationBody>;

export const SIM_TRACE_KINDS = ['enter', 'advance', 'wait', 'timer', 'event', 'dispatch', 'exit'] as const;
export type SimTraceKind = (typeof SIM_TRACE_KINDS)[number];

/**
 * One moment of the run, in order: `enter` (variant), `advance` (the port a step left through, with its outcome),
 * `wait` (timers and awaited events), `timer` (a wake-up fired), `event` (a scripted event and what it did),
 * `dispatch` (a Message step's send: status and the exact payload of each channel) and `exit` (how the entry ended).
 */
export interface SimTraceItem {
  at: string;
  /** Seconds since the simulation start. */
  offsetSeconds: number;
  stepId: string | null;
  kind: SimTraceKind;
  port?: string;
  payload?: Record<string, unknown>;
  reason?: string;
}

export interface SimulationOutcome {
  /**
   * `completed`, `exited`, `failed` — the entry finished; `waiting` — still in a step when the virtual clock reached
   * `untilDuration`; `rejected` — did not enter (`reason`: an entry rule); `limit_exceeded` (`reason`: `step_executions`)
   * or `timeout` — the run stopped early, the trace up to there is kept.
   */
  status: 'completed' | 'exited' | 'failed' | 'waiting' | 'rejected' | 'limit_exceeded' | 'timeout';
  reason?: string;
  /** The step the entry is in (waiting) or ended at. */
  stepId: string | null;
  variantId: string | null;
  /** Steps in the order the profile reached them (the path to highlight on the graph). */
  path: string[];
  startedAt: string;
  /** Virtual time the run ended at. */
  endedAt: string;
  stepExecutions: number;
  eventsProcessed: number;
  /** Scripted events after the end (the entry finished, or after `untilDuration`). */
  eventsSkipped: number;
  durationMs: number;
}

export interface SimulationResult {
  /** Names the run in logs; nothing is stored under it. */
  simulationId: string;
  definitionSource: 'draft' | 'published';
  deliveryMode: 'dry_run';
  profile: { kind: 'test'; profileId: string } | { kind: 'synthetic' };
  /** Personal attributes of the test profile were rendered as ••• (no `pii.view`). */
  masked: boolean;
  trace: SimTraceItem[];
  outcome: SimulationOutcome;
}
